// Vanilla SEO component (no react-helmet dependency). Manages meta tags,
// canonical, hreflang, OG, JSON-LD. All URLs are absolute and correctly
// prefixed with process.env.PUBLIC_URL (the sub-path under which the app is
// served — e.g. "/produits/pam-assesment").
import { useEffect } from "react";

// Production origin. Overridable at build time via REACT_APP_SITE_ORIGIN.
// Falls back to window.location.origin at runtime so preview keeps working.
const CONFIG_ORIGIN = (process.env.REACT_APP_SITE_ORIGIN || "").replace(/\/$/, "");
const PUBLIC_URL = (process.env.PUBLIC_URL || "").replace(/\/$/, "");

function siteOrigin() {
    if (CONFIG_ORIGIN) return CONFIG_ORIGIN;
    if (typeof window !== "undefined" && window.location) return window.location.origin;
    return "";
}

/** Build an absolute URL from a router-level path like "/fr" or "/fr/wizard". */
export function absoluteUrl(path = "/") {
    const clean = path.startsWith("/") ? path : `/${path}`;
    return `${siteOrigin()}${PUBLIC_URL}${clean}`;
}

function setMeta(name, content, attr = "name") {
    if (!content) return;
    let el = document.head.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function setLinkRel(rel, href, attrs = {}) {
    const key = `${rel}${attrs.hreflang ? `-${attrs.hreflang}` : ""}`;
    const selector = attrs.hreflang
        ? `link[rel="${rel}"][hreflang="${attrs.hreflang}"]`
        : `link[rel="${rel}"]:not([hreflang])`;
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        if (attrs.hreflang) el.setAttribute("hreflang", attrs.hreflang);
        el.dataset.seo = key;
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

function setJsonLd(id, data) {
    let el = document.getElementById(id);
    if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = id;
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
}

export default function Seo({
    title,
    description,
    path = "/",
    lang = "fr",
    image = "https://brand.ariovis.fr/logos/Logotype_BLACK.svg",
    type = "website",
    jsonLd = null,
    noindex = false,
}) {
    useEffect(() => {
        if (title) document.title = title;
        if (description) setMeta("description", description);

        const canonical = absoluteUrl(path);
        const enPath = path.replace(/^\/fr(?=\/|$)/, "/en");
        const frPath = path.replace(/^\/en(?=\/|$)/, "/fr");
        const enUrl = absoluteUrl(enPath);
        const frUrl = absoluteUrl(frPath);

        // Open Graph
        setMeta("og:type", type, "property");
        setMeta("og:title", title || "", "property");
        setMeta("og:description", description || "", "property");
        setMeta("og:site_name", "Ma Roadmap PAM · Ariovis", "property");
        setMeta("og:image", image, "property");
        setMeta("og:locale", lang === "fr" ? "fr_FR" : "en_GB", "property");
        setMeta("og:url", canonical, "property");

        // Twitter
        setMeta("twitter:card", "summary_large_image");
        setMeta("twitter:title", title || "");
        setMeta("twitter:description", description || "");
        setMeta("twitter:image", image);
        setMeta("twitter:url", canonical);

        setMeta("robots", noindex ? "noindex,nofollow" : "index,follow");
        setMeta("theme-color", "#9E0E40");

        // Canonical + hreflang
        setLinkRel("canonical", canonical);
        setLinkRel("alternate", frUrl, { hreflang: "fr" });
        setLinkRel("alternate", enUrl, { hreflang: "en" });
        setLinkRel("alternate", frUrl, { hreflang: "x-default" });

        // JSON-LD
        if (jsonLd) {
            setJsonLd("seo-jsonld", jsonLd);
        } else {
            const el = document.getElementById("seo-jsonld");
            if (el) el.textContent = "{}";
        }

        // Document lang
        document.documentElement.setAttribute("lang", lang);
    }, [title, description, path, lang, image, type, jsonLd, noindex]);

    return null;
}
